import { CURRENCY_SYMBOL } from './currency';
/**
 * ============================================================================
 * REVENUE FORMATTERS - SINGLE SOURCE OF TRUTH
 * ============================================================================
 *
 * ⚠️  CRITICAL: This is the ONLY place where formatting functions live!
 *
 * RULES:
 * 1. NEVER define formatCurrency or formatPercentage in components
 * 2. If you need to format numbers, dates, or currency, use THIS file
 * 3. All formatting must be consistent across the app
 *
 * ============================================================================
 */

/**
 * Formats currency to Nigerian Naira with no decimals
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  const num = parseFloat(String(amount || 0));
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
  return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Formats currency in compact form (K, M)
 */
export function formatCompactCurrency(amount: number | string): string {
  const num = parseFloat(String(amount));
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;

  if (num >= 1000000) {
    return `${CURRENCY_SYMBOL}${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${CURRENCY_SYMBOL}${(num / 1000).toFixed(0)}K`;
  }
  return formatCurrency(num);
}

/**
 * Formats percentage with % symbol
 */
export function formatPercentage(value: number | string): string {
  const num = parseFloat(String(value));
  if (isNaN(num)) return '0%';
  return `${num.toFixed(0)}%`;
}

/**
 * Formats date to readable format (e.g., "Jan 15, 2025")
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'N/A';

    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return 'N/A';
  }
}

/**
 * Formats time to readable format (e.g., "3:30 PM")
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return 'N/A';

  try {
    // Handle HH:mm:ss format
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time;

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time;
  }
}

/**
 * Formats date range display
 */
export function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate && !endDate) return 'All Time';
  if (startDate && !endDate) return `From ${formatDate(startDate)}`;
  if (!startDate && endDate) return `Until ${formatDate(endDate)}`;
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

/**
 * Formats number with commas (no currency symbol)
 */
export function formatNumber(value: number | string): string {
  const num = parseFloat(String(value));
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-NG');
}

/**
 * Formats payout status to readable text with color class
 */
export function getPayoutStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'processing':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

/**
 * Formats transaction status to readable text with color class
 */
export function getTransactionStatusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'active':
      return 'bg-blue-100 text-blue-800';
    case 'refunded':
      return 'bg-purple-100 text-purple-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Gets payment status indicator color (for dots)
 */
export function getPaymentStatusDotColor(paymentStatus: string): string {
  switch (paymentStatus) {
    case 'successful':
      return 'bg-green-500';
    case 'refunded':
      return 'bg-purple-500';
    case 'pending':
      return 'bg-yellow-500';
    case 'failed':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

/**
 * Formats relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'N/A';

    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffMins = Math.floor(Math.abs(diffMs) / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    const isPast = diffMs < 0;

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return isPast ? `${diffMins}m ago` : `in ${diffMins}m`;
    if (diffHours < 24) return isPast ? `${diffHours}h ago` : `in ${diffHours}h`;
    if (diffDays < 7) return isPast ? `${diffDays}d ago` : `in ${diffDays}d`;

    return formatDate(d);
  } catch (error) {
    return 'N/A';
  }
}

/**
 * Calculates and formats percentage change with +/- indicator
 */
export function formatPercentageChange(current: number, previous: number): {
  value: string;
  isPositive: boolean;
  isNeutral: boolean;
} {
  if (previous === 0) {
    return {
      value: current > 0 ? '+100%' : '0%',
      isPositive: current > 0,
      isNeutral: current === 0,
    };
  }

  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isNeutral = change === 0;

  return {
    value: `${isPositive ? '+' : ''}${change.toFixed(1)}%`,
    isPositive,
    isNeutral,
  };
}
